import deepMapKeys from 'deep-map-keys'
import { snakeToCamelCase } from 'libs/utils/string'

const BASE_HEADERS: Record<string, string> = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

// Cloudflare Access headers for requests to protected origins (e.g. masterclass.dev when proxied)
const CF_ACCESS_HEADERS: Record<string, string> =
  typeof process.env.NEXT_PUBLIC_DEV_CF_ACCESS_CLIENT_ID === 'string' &&
  typeof process.env.NEXT_PUBLIC_DEV_CF_ACCESS_CLIENT_SECRET === 'string'
    ? {
        'CF-Access-Client-Id': process.env.NEXT_PUBLIC_DEV_CF_ACCESS_CLIENT_ID,
        'CF-Access-Client-Secret':
          process.env.NEXT_PUBLIC_DEV_CF_ACCESS_CLIENT_SECRET,
      }
    : {}

export class APIError extends Error {
  response: unknown

  constructor(path: string, response: unknown) {
    super(`Error(s) returned from endpoint ${path}`)
    this.name = 'APIError'
    this.response = response
  }
}

const buildPath = (path: string): string | URL =>
  typeof process.env.API_URL === 'string'
    ? new URL(path, process.env.API_URL)
    : path

export const csrf = async (): Promise<string> => {
  const path = '/api/v2/csrf-token'
  const response = await fetch(buildPath(path), {
    headers: { ...BASE_HEADERS, ...CF_ACCESS_HEADERS },
  })
  const { token } = await response.json()

  if ((response as unknown as Record<string, unknown>).errors) {
    throw new APIError(path, response)
  }

  return token
}

export const get = async (path = '') => {
  const request = await fetch(buildPath(path), {
    headers: {
      ...BASE_HEADERS,
      ...CF_ACCESS_HEADERS,
    },
    credentials: 'include',
  })

  const response = await request.json()

  if (response.errors) {
    throw new APIError(path, response)
  }

  const mapped = deepMapKeys(response, snakeToCamelCase)

  return mapped
}

export const post = async (path: string, body: unknown) => {
  const token = await csrf()
  const request = await fetch(buildPath(path), {
    method: 'post',
    headers: {
      ...BASE_HEADERS,
      ...CF_ACCESS_HEADERS,
      'X-CSRF-Token': token,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })

  try {
    const response = await request.json()

    if (response.errors) {
      throw new APIError(path, response)
    }

    const mapped = deepMapKeys(response, snakeToCamelCase)

    return mapped
  } catch (e) {
    return {}
  }
}

export const put = async (path: string, body?: unknown) => {
  const token = await csrf()

  const request = await fetch(buildPath(path), {
    method: 'put',
    headers: {
      ...BASE_HEADERS,
      ...CF_ACCESS_HEADERS,
      'X-CSRF-Token': token,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })

  const response = await request.json()

  if (response.errors) {
    throw new APIError(path, response)
  }

  const mapped = deepMapKeys(response, snakeToCamelCase)

  return mapped
}
